'use client';
import { useState, useCallback, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
    DollarSign, TrendingUp, TrendingDown, Download, RefreshCw, FolderOpen, BarChart3,
    CheckCircle, AlertTriangle, Sparkles, Upload, FileSpreadsheet, X, Server,
    Layers, ShieldCheck, Lightbulb, Target, ArrowDownRight, ArrowUpRight, Building2
} from 'lucide-react';
import { useTheme } from '@/components/ThemeContext';
import { AgentPageLayout } from '@/components/AgentPageLayout';
import { AGENT_CONFIGS } from '@/lib/agent-constants';
import { AgentToolbar } from '@/components/AgentToolbar';

interface InputInfo {
    source: 'excel' | 'manual';
    fileName?: string;
    projectInfo?: { name: string; type: string; area: number; grade: string };
    excelSummary?: { sheetName: string; headers: string[]; totalRows: number };
}
interface CostBreakdown {
    category: string; amount: number; ratio: number; itemCount: number; unitCost: number;
}
interface TopItem {
    rank: number; name: string; category: string; amount: number; unit: string; qty: number; unitPrice: number;
}
interface VEProposal {
    title: string; currentCost: number; proposedCost: number; saving: number; savingRate: number; risk: string; detail: string;
}
interface RiskFactor {
    factor: string; impact: string; probability: string; mitigation: string;
}
interface AnalysisResult {
    projectSummary: {
        name: string; type: string; totalArea: number; grade: string;
        totalCost: number; unitCostPerSqm: number; constructionPeriod: string;
    };
    costBreakdown: CostBreakdown[];
    topItems: TopItem[];
    veProposals: VEProposal[];
    benchmark: { avgCostPerSqm: number; position: string; comment: string };
    riskFactors: RiskFactor[];
}
interface NasFile {
    name: string; path: string; size: number;
}

const COST_PROJECT_TYPES = [
 { value: '오피스', label: ' 오피스' },
 { value: '학교', label: ' 학교' },
 { value: '도서관', label: ' 도서관' },
 { value: '병원', label: ' 병원' },
 { value: '상업시설', label: ' 상업시설' },
 { value: '주거', label: ' 주거' },
 { value: '문화시설', label: ' 문화시설' },
 { value: '체육시설', label: ' 체육시설' },
];

const GRADES = [
 { value: '프리미엄', label: ' 프리미엄', desc: '최고급 자재 · 특수설비' },
 { value: '표준', label: ' 표준', desc: '일반적인 품질 수준' },
 { value: '경제', label: ' 경제', desc: '비용 최적화 중심' },
];

export default function CostAgentPage() {
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const config = AGENT_CONFIGS.cost;
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [inputInfo, setInputInfo] = useState<InputInfo | null>(null);
    const [analysis, setAnalysis] = useState<AnalysisResult | null>(null);
    const [streamText, setStreamText] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [stage, setStage] = useState(0);
    const [aiDone, setAiDone] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const [nasFiles, setNasFiles] = useState<NasFile[]>([]);
    const [nasConnected, setNasConnected] = useState(false);
    const [showNas, setShowNas] = useState(false);
    const [error, setError] = useState('');
    const [mode, setMode] = useState<'upload' | 'manual'>('manual');

    // 프로젝트 정보 입력
    const [projectName, setProjectName] = useState('');
    const [projectType, setProjectType] = useState('오피스');
    const [area, setArea] = useState('640');
    const [grade, setGrade] = useState('표준');

    const loadNasFiles = useCallback(async () => {
        try {
            const res = await fetch('/api/cost-agent');
            const data = await res.json();
            setNasConnected(data.nasConnected);
            setNasFiles(data.files || []);
            setShowNas(true);
        } catch { setError('NAS 연결 실패'); }
    }, []);

    // Excel 파일 분석
    const analyzeExcel = useCallback(async (file?: File, nasPath?: string) => {
        setError(''); setAnalysis(null); setStreamText(''); setStage(1); setIsProcessing(true); setAiDone(false);
        try {
            const formData = new FormData();
            if (file) formData.append('file', file);
            if (nasPath) formData.append('nasPath', nasPath);

            const res = await fetch('/api/cost-agent', { method: 'POST', body: formData });
            if (!res.ok) { const err = await res.json(); setError(err.error || 'API 오류'); setStage(0); setIsProcessing(false); return; }

            await processStream(res);
        } catch (e: any) { setError(e.message || '분석 실패'); setStage(0); }
        finally { setIsProcessing(false); }
    }, []);

    // 프로젝트 정보 기반 분석
    const analyzeProject = useCallback(async () => {
        setError(''); setAnalysis(null); setStreamText(''); setStage(1); setIsProcessing(true); setAiDone(false);
        try {
            const res = await fetch('/api/cost-agent', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    projectName: projectName || `${projectType} 프로젝트`,
                    projectType,
                    area: parseInt(area) || 640,
                    grade,
                }),
            });
            if (!res.ok) { const err = await res.json(); setError(err.error || 'API 오류'); setStage(0); setIsProcessing(false); return; }

            await processStream(res);
        } catch (e: any) { setError(e.message || '분석 실패'); setStage(0); }
        finally { setIsProcessing(false); }
    }, [projectName, projectType, area, grade]);

    // SSE 스트림 처리
    const processStream = async (res: Response) => {
        setStage(2);
        const reader = res.body?.getReader();
        if (!reader) return;
        const decoder = new TextDecoder();
        let fullText = '';

        while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            const chunk = decoder.decode(value, { stream: true });
            for (const line of chunk.split('\n')) {
                if (!line.startsWith('data: ')) continue;
                try {
                    const data = JSON.parse(line.substring(6));
                    if (data.type === 'input_info') { setInputInfo(data as InputInfo); setStage(3); }
                    else if (data.type === 'chunk') { fullText += data.text; setStreamText(fullText); }
                    else if (data.type === 'complete') {
                        try {
                            const jsonMatch = data.fullText.match(/\{[\s\S]*\}/);
                            if (jsonMatch) setAnalysis(JSON.parse(jsonMatch[0]));
                        } catch { /* text only */ }
                        setStage(4);
                        setAiDone(true);
                    }
                } catch { /* skip */ }
            }
        }
    };

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault(); setDragOver(false);
        const file = e.dataTransfer.files[0];
        if (file && /\.(xlsx?|csv)$/i.test(file.name)) analyzeExcel(file);
        else setError('Excel 파일(.xlsx, .xls, .csv)만 지원합니다');
    }, [analyzeExcel]);

    const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) analyzeExcel(file);
    }, [analyzeExcel]);

    const formatAmount = (n: number) => {
        if (!n) return '₩0';
        if (n >= 100000000) return `₩${(n / 100000000).toFixed(1)}억`;
        if (n >= 10000) return `₩${(n / 10000).toFixed(0)}만`;
        return `₩${n.toLocaleString()}`;
    };

    const riskColor = (level: string) => {
        if (level === 'high') return isDark ? 'text-red-400 bg-red-500/10' : 'text-red-600 bg-red-50';
        if (level === 'medium') return isDark ? 'text-amber-400 bg-amber-500/10' : 'text-amber-600 bg-amber-50';
        return isDark ? 'text-emerald-400 bg-emerald-500/10' : 'text-emerald-600 bg-emerald-50';
    };

    const reset = () => {
        setStage(0); setInputInfo(null); setAnalysis(null); setStreamText(''); setAiDone(false);
    };

    return (
        <AgentPageLayout config={config} icon={<DollarSign className="w-5 h-5" />} isProcessing={isProcessing} aiDone={aiDone}>
            {/* Error */}
            <AnimatePresence>
                {error && (
                    <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                        className={`mb-4 p-3 rounded-xl flex items-center gap-2 text-sm ${isDark ? 'bg-red-500/10 text-red-300 border border-red-500/20' : 'bg-red-50 text-red-600 border border-red-200'}`}>
                        <AlertTriangle className="w-4 h-4 shrink-0" /> {error}
                        <button onClick={() => setError('')} className="ml-auto"><X className="w-4 h-4" /></button>
                    </motion.div>
                )}
            </AnimatePresence>

            {stage === 0 ? (
                /* ═══ Input Zone ═══ */
                <div className="space-y-4">
                    {/* Mode Toggle */}
                    <div className={`flex gap-1 p-1 rounded-xl w-fit ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
                        <button onClick={() => setMode('manual')}
                            className={`px-4 py-2 rounded-lg text-xs font-bold transition cursor-pointer ${
                                mode === 'manual' ? isDark ? `${config.accentBg} ${config.accentText}` : 'bg-white text-emerald-700 shadow-sm' : isDark ? 'text-gray-400' : 'text-gray-500'
                            }`}><Building2 className="w-3.5 h-3.5 inline mr-1.5" />프로젝트 정보 입력</button>
                        <button onClick={() => setMode('upload')}
                            className={`px-4 py-2 rounded-lg text-xs font-bold transition cursor-pointer ${
                                mode === 'upload' ? isDark ? `${config.accentBg} ${config.accentText}` : 'bg-white text-emerald-700 shadow-sm' : isDark ? 'text-gray-400' : 'text-gray-500'
                            }`}><FileSpreadsheet className="w-3.5 h-3.5 inline mr-1.5" />Excel 내역서 분석</button>
                    </div>

                    {mode === 'manual' ? (
                        /* Manual Input */
                        <div className={`rounded-2xl border p-6 ${isDark ? 'border-white/10 bg-gray-900/50' : 'border-gray-200 bg-white shadow-sm'}`}>
                            <div className="grid sm:grid-cols-2 gap-4 mb-5">
                                <div>
                                    <label className={`text-[10px] font-bold uppercase mb-1.5 block ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>프로젝트명</label>
                                    <input value={projectName} onChange={e => setProjectName(e.target.value)}
                                        placeholder="예: 서울 강남 A 오피스 리모델링"
                                        className={`w-full px-3.5 py-2.5 rounded-xl text-sm border transition ${isDark ? 'bg-white/5 border-white/10 focus:border-emerald-500/50 text-white placeholder:text-gray-600' : 'bg-gray-50 border-gray-200 focus:border-emerald-400 placeholder:text-gray-300'} outline-none`} />
                                </div>
                                <div>
                                    <label className={`text-[10px] font-bold uppercase mb-1.5 block ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>연면적 (㎡)</label>
                                    <input type="number" value={area} onChange={e => setArea(e.target.value)}
                                        className={`w-full px-3.5 py-2.5 rounded-xl text-sm border transition ${isDark ? 'bg-white/5 border-white/10 focus:border-emerald-500/50 text-white' : 'bg-gray-50 border-gray-200 focus:border-emerald-400'} outline-none`} />
                                </div>
                            </div>
                            {/* Project Type */}
                            <div className="mb-4">
                                <label className={`text-[10px] font-bold uppercase mb-1.5 block ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>프로젝트 유형</label>
                                <div className="flex flex-wrap gap-2">
                                    {COST_PROJECT_TYPES.map(pt => (
                                        <button key={pt.value} onClick={() => setProjectType(pt.value)}
                                            className={`px-3 py-2 rounded-xl text-xs font-medium transition cursor-pointer border ${
                                                projectType === pt.value
                                                    ? isDark ? `${config.accentBg} ${config.accentBorder} ${config.accentText}` : 'bg-emerald-50 border-emerald-300 text-emerald-700'
                                                    : isDark ? 'bg-white/[0.03] border-white/5 text-gray-400 hover:bg-white/5' : 'bg-gray-50 border-gray-100 text-gray-500 hover:bg-gray-100'
                                            }`}>{pt.label}</button>
                                    ))}
                                </div>
                            </div>
                            {/* Grade */}
                            <div className="mb-5">
                                <label className={`text-[10px] font-bold uppercase mb-1.5 block ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>공사 등급</label>
                                <div className="grid grid-cols-3 gap-2">
                                    {GRADES.map(g => (
                                        <button key={g.value} onClick={() => setGrade(g.value)}
                                            className={`p-3 rounded-xl text-left transition cursor-pointer border ${
                                                grade === g.value
                                                    ? isDark ? `${config.accentBg} ${config.accentBorder}` : 'bg-emerald-50 border-emerald-300'
                                                    : isDark ? 'bg-white/[0.03] border-white/5 hover:bg-white/5' : 'bg-gray-50 border-gray-100 hover:bg-gray-100'
                                            }`}>
                                            <div className="text-xs font-bold">{g.label}</div>
                                            <div className={`text-[10px] mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{g.desc}</div>
                                        </button>
                                    ))}
                                </div>
                            </div>
                            {/* Submit */}
                            <button onClick={analyzeProject} disabled={isProcessing}
                                className={`w-full py-3.5 rounded-xl bg-gradient-to-r ${config.gradient} text-white text-sm font-bold cursor-pointer transition hover:shadow-lg disabled:opacity-50 flex items-center justify-center gap-2`}>
                                {isProcessing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                                AI 개략공사비 산출
                            </button>
                        </div>
                    ) : (
                        /* Upload Mode */
                        <div className="grid md:grid-cols-2 gap-4">
                            <div onDragOver={e => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)} onDrop={handleDrop}
                                onClick={() => fileInputRef.current?.click()}
                                className={`rounded-2xl border-2 border-dashed p-12 text-center cursor-pointer transition-all ${
                                    dragOver ? isDark ? `border-emerald-400 ${config.accentBg}` : 'border-emerald-400 bg-emerald-50'
                                    : isDark ? 'border-white/10 bg-gray-900/50 hover:border-emerald-500/30' : 'border-gray-200 bg-white hover:border-emerald-300'
                                }`}>
                                <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleFileSelect} className="hidden" />
                                <Upload className={`w-12 h-12 mx-auto mb-4 ${dragOver ? 'text-emerald-400' : isDark ? 'text-emerald-500/30' : 'text-emerald-300'}`} />
                                <p className="font-bold mb-1">BOQ 내역서 업로드</p>
                                <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                    .xlsx / .xls / .csv 파일을 드래그하거나 클릭하세요
                                </p>
                                <div className={`mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold ${isDark ? `${config.accentBg} ${config.accentText}` : 'bg-emerald-50 text-emerald-700'}`}>
                                    <FileSpreadsheet className="w-4 h-4" /> 파일 선택
                                </div>
                            </div>
                            {/* NAS */}
                            <div className={`rounded-2xl border p-6 ${isDark ? 'border-white/10 bg-gray-900/50' : 'border-gray-200 bg-white shadow-sm'}`}>
                                <div className="flex items-center gap-2 mb-4">
                                    <Server className="w-5 h-5 text-green-400" />
                                    <span className="font-bold text-sm">NAS 내역서 탐색</span>
                                    {nasConnected && <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />}
                                </div>
                                <button onClick={loadNasFiles}
                                    className={`w-full py-3 rounded-xl text-xs font-bold transition cursor-pointer flex items-center justify-center gap-2 ${isDark ? 'bg-green-500/10 text-green-300 hover:bg-green-500/20 border border-green-500/20' : 'bg-green-50 text-green-600 hover:bg-green-100 border border-green-200'}`}>
                                    <FolderOpen className="w-4 h-4" /> NAS에서 내역서 찾기
                                </button>
                                <AnimatePresence>
                                    {showNas && (
                                        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} className="mt-3 space-y-1.5 overflow-hidden">
                                            {nasFiles.length === 0 ? (
                                                <p className={`text-xs text-center py-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                                    {nasConnected ? '내역서 파일이 없습니다' : 'NAS 미연결'}
                                                </p>
                                            ) : nasFiles.map(f => (
                                                <button key={f.path} onClick={() => analyzeExcel(undefined, f.path)}
                                                    className={`w-full text-left px-3 py-2.5 rounded-xl text-[11px] transition cursor-pointer ${isDark ? 'hover:bg-white/5 border border-white/5' : 'hover:bg-gray-50 border border-gray-100'}`}>
                                                    <div className="flex items-center gap-2">
                                                        <FileSpreadsheet className="w-4 h-4 text-green-400 shrink-0" />
                                                        <div className="min-w-0 flex-1">
                                                            <div className="font-semibold truncate">{f.name}</div>
                                                            <div className={`text-[10px] ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>{(f.size/1024/1024).toFixed(1)} MB</div>
                                                        </div>
                                                    </div>
                                                </button>
                                            ))}
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                /* ═══ Analysis Results ═══ */
                <div className="space-y-4">
                    {/* Streaming */}
                    {stage >= 3 && !analysis && (
                        <div className={`rounded-2xl border p-5 ${isDark ? 'border-white/10 bg-white/[0.02]' : 'border-gray-200 bg-white shadow-sm'}`}>
                            <div className="flex items-center gap-2 mb-3">
                                <Sparkles className={`w-4 h-4 ${config.accentText} animate-pulse`} />
                                <span className="text-sm font-bold">AI 원가관리 전문가 분석 중...</span>
                            </div>
                            <pre className={`text-xs whitespace-pre-wrap font-mono leading-relaxed max-h-[400px] overflow-y-auto ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                {streamText || '프로젝트 정보를 Gemini에 전송 중...'}
                            </pre>
                        </div>
                    )}

                    {/* Final Results */}
                    {analysis && (
                        <>
                            {/* Project Summary */}
                            <div className={`rounded-2xl border p-4 ${isDark ? `${config.accentBorder} ${config.accentBg}` : 'border-emerald-200 bg-emerald-50'}`}>
                                <div className="flex items-center gap-3 mb-3">
                                    <Building2 className={`w-6 h-6 ${config.accentText}`} />
                                    <div>
                                        <h2 className="font-bold text-lg">{analysis.projectSummary?.name || '프로젝트'}</h2>
                                        <p className={`text-xs ${isDark ? 'text-emerald-300/60' : 'text-emerald-600'}`}>
                                            {analysis.projectSummary?.type} · {analysis.projectSummary?.totalArea?.toLocaleString()}㎡ · {analysis.projectSummary?.grade} · {analysis.projectSummary?.constructionPeriod || ''}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Summary Cards */}
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                {[
                                    { label: '총 공사비', value: formatAmount(analysis.projectSummary?.totalCost), color: 'text-emerald-400', icon: <DollarSign className="w-4 h-4" /> },
                                    { label: '㎡당 단가', value: `₩${(analysis.projectSummary?.unitCostPerSqm || 0).toLocaleString()}`, color: 'text-cyan-400', icon: <BarChart3 className="w-4 h-4" /> },
                                    { label: '공종 수', value: `${analysis.costBreakdown?.length || 0}개`, color: 'text-violet-400', icon: <Layers className="w-4 h-4" /> },
                                    { label: 'VE 제안', value: `${analysis.veProposals?.length || 0}건`, color: 'text-amber-400', icon: <Lightbulb className="w-4 h-4" /> },
                                ].map((s, i) => (
                                    <div key={i} className={`rounded-xl border p-3 ${isDark ? 'bg-gray-900/80 border-white/10' : 'bg-white border-gray-200 shadow-sm'}`}>
                                        <div className={`flex items-center gap-1.5 mb-1 ${s.color}`}>{s.icon}</div>
                                        <div className={`text-lg font-bold ${s.color}`}>{s.value}</div>
                                        <div className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{s.label}</div>
                                    </div>
                                ))}
                            </div>

                            {/* Cost Breakdown */}
                            {analysis.costBreakdown && analysis.costBreakdown.length > 0 && (
                                <div className={`rounded-2xl border overflow-hidden ${isDark ? 'border-white/10 bg-gray-900/80' : 'border-gray-200 bg-white shadow-sm'}`}>
                                    <div className={`px-4 py-2.5 border-b flex items-center justify-between ${isDark ? 'border-white/10 bg-white/[0.02]' : 'border-gray-100 bg-gray-50'}`}>
                                        <span className="text-xs font-bold flex items-center gap-2">
                                            <BarChart3 className={`w-4 h-4 ${config.accentText}`} /> 공종별 원가 분석
                                        </span>
                                    </div>
                                    <div className="p-4 space-y-2.5">
                                        {analysis.costBreakdown.map((cat, i) => (
                                            <div key={i} className="flex items-center gap-3">
                                                <span className="text-[11px] font-bold w-16 truncate">{cat.category}</span>
                                                <div className={`flex-1 h-5 rounded-full overflow-hidden ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
                                                    <div className={`h-full rounded-full bg-gradient-to-r ${config.gradient} transition-all flex items-center justify-end pr-1`}
                                                        style={{ width: `${Math.min(100, Math.max(5, cat.ratio || 0))}%` }}>
                                                        {(cat.ratio || 0) > 15 && <span className="text-[8px] text-white font-bold">{(cat.ratio || 0).toFixed(1)}%</span>}
                                                    </div>
                                                </div>
                                                <span className={`text-[10px] font-mono w-12 text-right ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{(cat.ratio || 0).toFixed(1)}%</span>
                                                <span className={`text-[10px] font-mono ${config.accentText} w-20 text-right`}>{formatAmount(cat.amount)}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Top Items Table */}
                            {analysis.topItems && analysis.topItems.length > 0 && (
                                <div className={`rounded-2xl border overflow-hidden ${isDark ? 'border-white/10 bg-gray-900/80' : 'border-gray-200 bg-white shadow-sm'}`}>
                                    <div className={`px-4 py-2.5 border-b ${isDark ? 'border-white/10 bg-white/[0.02]' : 'border-gray-100 bg-gray-50'}`}>
                                        <span className="text-xs font-bold flex items-center gap-2">
                                            <TrendingUp className="w-4 h-4 text-cyan-400" /> 주요 항목 (Top {analysis.topItems.length})
                                        </span>
                                    </div>
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-[10px]">
                                            <thead><tr className={isDark ? 'bg-white/[0.03]' : 'bg-gray-50'}>
                                                {['#', '항목', '공종', '수량', '단가', '금액'].map(h => <th key={h} className={`px-3 py-2 font-bold text-left ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{h}</th>)}
                                            </tr></thead>
                                            <tbody>{analysis.topItems.map((item, i) => (
                                                <tr key={i} className={`border-t ${isDark ? 'border-white/5 hover:bg-white/[0.03]' : 'border-gray-100 hover:bg-gray-50'}`}>
                                                    <td className={`px-3 py-2 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>{item.rank || i+1}</td>
                                                    <td className="px-3 py-2 font-semibold">{item.name}</td>
                                                    <td className="px-3 py-2"><span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>{item.category}</span></td>
                                                    <td className="px-3 py-2 text-right font-mono">{item.qty?.toLocaleString()} {item.unit}</td>
                                                    <td className="px-3 py-2 text-right font-mono">₩{item.unitPrice?.toLocaleString()}</td>
                                                    <td className={`px-3 py-2 text-right font-mono font-bold ${config.accentText}`}>{formatAmount(item.amount)}</td>
                                                </tr>
                                            ))}</tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {/* VE Proposals */}
                            {analysis.veProposals && analysis.veProposals.length > 0 && (
                                <div className={`rounded-2xl border overflow-hidden ${isDark ? 'border-white/10 bg-gray-900/80' : 'border-gray-200 bg-white shadow-sm'}`}>
                                    <div className={`px-4 py-2.5 border-b ${isDark ? 'border-white/10 bg-amber-500/5' : 'border-gray-100 bg-amber-50'}`}>
                                        <span className="text-xs font-bold text-amber-400 flex items-center gap-2">
                                            <Lightbulb className="w-4 h-4" /> VE(Value Engineering) 제안 ({analysis.veProposals.length}건)
                                        </span>
                                    </div>
                                    <div className={`divide-y ${isDark ? 'divide-white/5' : 'divide-gray-100'}`}>
                                        {analysis.veProposals.map((ve, i) => (
                                            <div key={i} className="px-4 py-3">
                                                <div className="flex items-center gap-2 mb-1.5">
                                                    <span className="text-xs font-bold">{ve.title}</span>
                                                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${riskColor(ve.risk)}`}>
                                                        RISK: {ve.risk?.toUpperCase()}
                                                    </span>
                                                    {ve.savingRate > 0 && (
                                                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ml-auto ${isDark ? 'bg-emerald-500/10 text-emerald-400' : 'bg-emerald-50 text-emerald-600'}`}>
                                                            <ArrowDownRight className="w-3 h-3 inline" /> {ve.savingRate?.toFixed(1)}% 절감
                                                        </span>
                                                    )}
                                                </div>
                                                <p className={`text-[11px] mb-1.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{ve.detail}</p>
                                                <div className="flex items-center gap-4 text-[10px] font-mono">
                                                    <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>현재: {formatAmount(ve.currentCost)}</span>
                                                    <span className={config.accentText}>→ 대안: {formatAmount(ve.proposedCost)}</span>
                                                    <span className={`font-bold ${config.accentText}`}>절감: {formatAmount(ve.saving)}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Benchmark */}
                            {analysis.benchmark && (
                                <div className={`rounded-2xl border p-4 ${isDark ? 'border-white/10 bg-white/[0.02]' : 'border-gray-200 bg-white shadow-sm'}`}>
                                    <div className="flex items-center gap-2 mb-2">
                                        <Target className="w-4 h-4 text-cyan-400" />
                                        <span className="text-xs font-bold">벤치마크 비교</span>
                                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ml-2 ${
                                            analysis.benchmark.position === 'within' ? isDark ? 'bg-emerald-500/10 text-emerald-400' : 'bg-emerald-50 text-emerald-600'
                                            : analysis.benchmark.position === 'above' ? isDark ? 'bg-amber-500/10 text-amber-400' : 'bg-amber-50 text-amber-600'
                                            : isDark ? 'bg-blue-500/10 text-blue-400' : 'bg-blue-50 text-blue-600'
                                        }`}>
                                            {analysis.benchmark.position === 'within' ? '적정 범위' : analysis.benchmark.position === 'above' ? '평균 이상' : '평균 이하'}
                                        </span>
                                    </div>
                                    <p className={`text-[11px] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                        유사 프로젝트 평균 단가: ₩{(analysis.benchmark.avgCostPerSqm || 0).toLocaleString()}/㎡ — {analysis.benchmark.comment}
                                    </p>
                                </div>
                            )}

                            {/* Risk Factors */}
                            {analysis.riskFactors && analysis.riskFactors.length > 0 && (
                                <div className={`rounded-2xl border overflow-hidden ${isDark ? 'border-white/10 bg-gray-900/80' : 'border-gray-200 bg-white shadow-sm'}`}>
                                    <div className={`px-4 py-2.5 border-b ${isDark ? 'border-white/10 bg-red-500/5' : 'border-gray-100 bg-red-50'}`}>
                                        <span className="text-xs font-bold text-red-400 flex items-center gap-2">
                                            <ShieldCheck className="w-4 h-4" /> 원가 리스크 분석 ({analysis.riskFactors.length}건)
                                        </span>
                                    </div>
                                    <div className={`divide-y ${isDark ? 'divide-white/5' : 'divide-gray-100'}`}>
                                        {analysis.riskFactors.map((rf, i) => (
                                            <div key={i} className="px-4 py-3">
                                                <div className="flex items-center gap-2 mb-0.5">
                                                    <span className="text-xs font-bold">{rf.factor}</span>
                                                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${riskColor(rf.impact)}`}>영향: {rf.impact?.toUpperCase()}</span>
                                                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${riskColor(rf.probability)}`}>확률: {rf.probability?.toUpperCase()}</span>
                                                </div>
                                                <p className={`text-[11px] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>대응: {rf.mitigation}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Toolbar */}
                            <AgentToolbar title="원가 분석" content={streamText} agentType="cost" />

                            {/* Reset */}
                            <div className="flex justify-center">
                                <button onClick={reset}
                                    className={`px-6 py-2.5 rounded-xl text-xs font-bold cursor-pointer transition ${isDark ? 'bg-white/5 text-gray-400 hover:bg-white/10' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
                                    <RefreshCw className="w-3.5 h-3.5 inline mr-2" /> 새 분석 시작
                                </button>
                            </div>
                        </>
                    )}
                </div>
            )}
        </AgentPageLayout>
    );
}
